import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
  ShoppingBag,
  ShoppingCart,
  LocalOffer,
  Favorite,
  LocalShipping,
  CardGiftcard,
} from '@mui/icons-material';

// Import SVG illustrations
import Illustration1 from '../assets/hero-1a.svg';
import Illustration2 from '../assets/hero-2a.svg';
import Illustration3 from '../assets/hero-3a.svg';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const slides = [
    {
      id: 1,
      title: 'Discover Amazing Deals Near You',
      subtitle:
        'Find the best discounts and offers from local vendors. Save money on everything you love!',
      button: 'Explore Deals',
      illustration: Illustration1,
    },
    {
      id: 2,
      title: 'Save Big on Your Favorite Brands',
      subtitle:
        'Browse exclusive deals from restaurants, shops, and services in your city.',
      button: 'Browse Offers',
      illustration: Illustration2,
    },
    {
      id: 3,
      title: 'Never Miss a Deal Again',
      subtitle:
        'Get real-time notifications for flash sales and limited-time offers near you.',
      button: 'Start Saving',
      illustration: Illustration3,
    },
  ];

  const backgroundIcons = [
    { Icon: ShoppingBag, top: '10%', left: '5%', size: 40, opacity: 0.15 },
    { Icon: ShoppingCart, top: '15%', right: '8%', size: 45, opacity: 0.18 },
    { Icon: LocalOffer, top: '35%', left: '3%', size: 35, opacity: 0.16 },
    { Icon: LocalShipping, top: '50%', right: '5%', size: 50, opacity: 0.15 },
    { Icon: CardGiftcard, top: '70%', left: '8%', size: 38, opacity: 0.18 },
    { Icon: Favorite, top: '75%', right: '10%', size: 42, opacity: 0.15 },
    { Icon: ShoppingBag, top: '40%', right: '15%', size: 36, opacity: 0.16 },
    { Icon: LocalOffer, top: '60%', left: '12%', size: 44, opacity: 0.18 },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <>
      <Box
        sx={{
          minHeight: { xs: '500px', md: '700px' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          background: `
            linear-gradient(135deg, #0f5328 0%, #1a7f3a 25%, #2d9e4a 50%, #4caf50 75%, #66bb6a 100%),
            radial-gradient(circle at 15% 25%, rgba(255,255,255,0.25) 0%, transparent 50%),
            radial-gradient(circle at 85% 65%, rgba(255,255,255,0.2) 0%, transparent 50%),
            radial-gradient(ellipse at 35% 15%, rgba(76,175,80,0.3) 0%, transparent 60%),
            radial-gradient(circle at 65% 85%, rgba(26,127,58,0.25) 0%, transparent 50%)
          `,
          backgroundSize:
            '400% 400%, 250px 250px, 350px 350px, 300px 200px, 220px 220px',
          animation: 'heroShift 6s ease infinite',
        }}
      >
        {/* Desktop Shapes */}
        {!isMobile && (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: '12%',
                left: '6%',
                width: 180,
                height: 180,
                background:
                  'radial-gradient(circle, rgba(76,175,80,0.5) 0%, rgba(26,127,58,0.2) 70%)',
                borderRadius: '50%',
                animation: 'float1 18s infinite linear',
                boxShadow: '0 0 40px rgba(76,175,80,0.6)',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '18%',
                right: '12%',
                width: 140,
                height: 140,
                background:
                  'radial-gradient(circle, rgba(255,215,0,0.6) 0%, rgba(255,193,7,0.3) 70%)',
                borderRadius: '50%',
                animation: 'float2 14s infinite linear',
                boxShadow: '0 0 40px rgba(255,215,0,0.7)',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '18%',
                right: '10%',
                width: 150,
                height: 150,
                background:
                  'radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 70%)',
                borderRadius: '50%',
                animation: 'float3 16s infinite linear',
                boxShadow: '0 0 30px rgba(255,255,255,0.5)',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '25%',
                left: '10%',
                width: 120,
                height: 120,
                background:
                  'linear-gradient(45deg, rgba(255,193,7,0.5) 0%, rgba(255,215,0,0.35) 100%)',
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                animation: 'float4 12s infinite linear',
                boxShadow: '0 0 30px rgba(255,215,0,0.6)',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '55%',
                left: '8%',
                width: 180,
                height: 80,
                background:
                  'linear-gradient(90deg, rgba(26,127,58,0.4) 0%, rgba(46,158,74,0.5) 50%, rgba(102,187,106,0.3) 100%)',
                borderRadius: '50px',
                animation: 'float5 22s infinite linear',
                boxShadow: '0 0 35px rgba(26,127,58,0.5)',
                zIndex: 1,
              }}
            />
          </>
        )}

        {/* Mobile Shapes */}
        {isMobile && (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: '15%',
                left: '10%',
                width: 90,
                height: 90,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.18)',
                animation: 'mobileFloat1 10s ease-in-out infinite',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '20%',
                right: '12%',
                width: 70,
                height: 70,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.14)',
                animation: 'mobileFloat2 12s ease-in-out infinite',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '55%',
                left: '60%',
                width: 110,
                height: 50,
                borderRadius: '50px',
                background: 'rgba(255,255,255,0.12)',
                animation: 'mobileFloat3 14s ease-in-out infinite',
                zIndex: 1,
              }}
            />
          </>
        )}

        {/* Background Icons */}
        {!isMobile &&
          backgroundIcons.map((item, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                top: item.top,
                left: item.left,
                right: item.right,
                zIndex: 2,
              }}
            >
              <item.Icon
                sx={{
                  fontSize: item.size,
                  color: 'rgba(255,255,255,0.9)',
                  opacity: item.opacity,
                }}
              />
            </Box>
          ))}

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 4,
              alignItems: 'center',
            }}
          >
            {/* Left Slide Content */}
            <Box
              key={currentSlide}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 2,
                animation: 'slideInLeft 0.8s ease-in-out',
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  fontSize: { xs: '1.8rem', md: '2.8rem' },
                  lineHeight: 1.2,
                  mb: 1,
                  textShadow: '2px 2px 8px rgba(0,0,0,0.3)',
                  transition: 'all 0.5s ease',
                }}
              >
                {currentSlideData.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255,255,255,0.95)',
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                  lineHeight: 1.6,
                  mb: 2,
                  textShadow: '1px 1px 4px rgba(0,0,0,0.2)',
                  transition: 'all 0.5s ease',
                }}
              >
                {currentSlideData.subtitle}
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  endIcon={<ChevronRight />}
                  onClick={() => (window.location.href = '/deals')}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.95)',
                    color: '#1a7f3a',
                    padding: '12px 32px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(255,255,255,0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(255,255,255,0.5)',
                    },
                  }}
                >
                  {currentSlideData.button}
                </Button>
              </Box>
            </Box>

            {/* Right Side Illustration */}
            {!isMobile && (
              <Box
                key={`illustration-${currentSlide}`}
                sx={{
                  position: 'relative',
                  height: '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'slideInRight 0.8s ease-in-out',
                }}
              >
                <Box
                  component="img"
                  src={currentSlideData.illustration}
                  alt={currentSlideData.title}
                  sx={{
                    width: '100%',
                    maxWidth: '450px',
                    height: 'auto',
                    filter: 'drop-shadow(0 15px 40px rgba(0,0,0,0.3))',
                    transition: 'all 0.5s ease',
                  }}
                />
              </Box>
            )}
          </Box>
        </Container>

        {/* Navigation Arrows */}
        <IconButton
          onClick={prevSlide}
          sx={{
            position: 'absolute',
            left: { xs: 10, md: 30 },
            top: '80%',
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.3)',
            zIndex: 10,
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.35)',
              transform: 'translateY(-50%) scale(1.1)',
            },
          }}
        >
          <ChevronLeft />
        </IconButton>

        <IconButton
          onClick={nextSlide}
          sx={{
            position: 'absolute',
            right: { xs: 10, md: 30 },
            top: '80%',
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.3)',
            zIndex: 10,
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.35)',
              transform: 'translateY(-50%) scale(1.1)',
            },
          }}
        >
          <ChevronRight />
        </IconButton>

        {/* Scroll Down Arrow */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            zIndex: 10,
            animation: 'bounce 2s infinite',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontWeight: 600,
              fontSize: '0.75rem',
              textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
            }}
          >
            Scroll to explore
          </Typography>
          <KeyboardArrowDown sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 24 }} />
        </Box>
      </Box>

      {/* Animations */}
      <style jsx>{`
        @keyframes heroShift {
          0%, 100% { background-position: 0% 50%, 20% 30%, 85% 65%, 35% 15%, 65% 85%; }
          25% { background-position: 100% 50%, 100% 100%, 15% 100%, 85% 50%, 35% 0%; }
          50% { background-position: 50% 100%, 0% 0%, 100% 100%, 0% 50%, 100% 50%; }
          75% { background-position: 0% 50%, 0% 100%, 85% 0%, 35% 100%, 65% 50%; }
        }

        @keyframes float1 { 0%,100%{transform:translate(0,0) rotate(0deg);}50%{transform:translate(40px,-40px) rotate(90deg);} }
        @keyframes float2 { 0%,100%{transform:translate(0,0) rotate(0deg);}33%{transform:translate(-50px,40px) rotate(-120deg);}66%{transform:translate(35px,-35px) rotate(-240deg);} }
        @keyframes float3 { 0%,100%{transform:translate(0,0) rotate(0deg);}33%{transform:translate(-30px,25px) rotate(120deg);}66%{transform:translate(20px,-20px) rotate(240deg);} }
        @keyframes float4 { 0%,100%{transform:translate(0,0) rotate(0deg);}50%{transform:translate(-25px,25px) rotate(180deg);} }
        @keyframes float5 { 0%,100%{transform:translate(0,0) scale(1);}33%{transform:translate(30px,-20px) scale(1.1);}66%{transform:translate(-25px,20px) scale(0.95);} }

        @keyframes mobileFloat1 { 0%,100%{transform:translateY(0);}50%{transform:translateY(-15px);} }
        @keyframes mobileFloat2 { 0%,100%{transform:translateY(0);}50%{transform:translateY(10px);} }
        @keyframes mobileFloat3 { 0%,100%{transform:translateY(0);}50%{transform:translateY(-12px);} }

        @keyframes slideInLeft { 0% {opacity:0; transform:translateX(-50px);} 100% {opacity:1; transform:translateX(0);} }
        @keyframes slideInRight { 0% {opacity:0; transform:translateX(50px);} 100% {opacity:1; transform:translateX(0);} }
        @keyframes bounce { 0%, 100% { transform: translateY(0);} 50% { transform: translateY(-10px); } }
      `}</style>
    </>
  );
};

export default Hero;
